import React from 'react';
import { defineMessages, type IntlShape } from 'react-intl-next';

import type { ExtensionManifest } from '@atlaskit/editor-common/extensions/extension-manifest';
import GridIcon from '@atlaskit/icon/core/grid';

import { EXTENSION_KEY, EXTENSION_TYPE } from '../nativeTabsConstants.ts';
import type { NativeTabsParameters } from '../nativeTabsTypes.ts';

const i18n = defineMessages({
	tabsExtensionTitle: {
		id: 'native-tabs.extension.title.non-final',
		defaultMessage: 'Tabs',
		description: 'Title of the Native Tabs extension shown in the slash-menu / quick-insert menu.',
	},
	tabsExtensionDescription: {
		id: 'native-tabs.extension.description.non-final',
		defaultMessage: 'Organize content on your page',
		description:
			'Description of the Native Tabs extension shown under the title in the slash-menu.',
	},
});

/**
 * Builds the shared parts of the Native Tabs `ExtensionManifest`:
 * `title`, `description`, `type`, `key`, and `icons`.
 *
 * The editor and renderer manifest factories spread this into their returned
 * manifest and add their own `modules` block (with or without `quickInsert`,
 * and with the appropriate `nodes.default.render` target).
 */
export const buildBaseManifest = (
	intl: IntlShape,
): Omit<ExtensionManifest<NativeTabsParameters>, 'modules'> => {
	const title = intl.formatMessage(i18n.tabsExtensionTitle);
	const description = intl.formatMessage(i18n.tabsExtensionDescription);

	// TODO: replace placeholder `grid` icon with the dedicated ADS Tabs icon
	// when one is published.
	const Icon = () => <GridIcon label={title} />;

	return {
		title,
		type: EXTENSION_TYPE,
		key: EXTENSION_KEY,
		description,
		icons: {
			'48': async () => Icon,
		},
	};
};
